import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["labelStack"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags';

// Import common utilities

import { getLighthouseMetrics } from '../additional-payload';
import { CHRReporter } from '../assets';
import * as bundleEvalTiming from '../bundle-eval-timing';
import coinflip from '../coinflip';
import { getConfig, getExperimentalInteractionRate, getUfoNameOverrides } from '../config';
import { getExperimentalVCMetrics } from '../create-experimental-interaction-metrics-payload';
import { getBm3Timings } from '../custom-timings';
import { getGlobalErrorCount } from '../global-error-handler';
import { getPageVisibilityState } from '../hidden-timing';
import * as initialPageLoadExtraTiming from '../initial-page-load-extra-timing';
import { interactionSpans as atlaskitInteractionSpans } from '../interaction-metrics';
import { createMemoryStateReport, createPressureStateReport } from '../machine-utilisation';
import * as resourceTiming from '../resource-timing';
import { filterResourceTimings } from '../resource-timing/common/utils/resource-timing-buffer';
import { roundEpsilon } from '../round-number';
import * as ssr from '../ssr';
import { buildSegmentTree, getOldSegmentsLabelStack, labelStackStartWith, optimizeLabelStack, sanitizeUfoName, stringifyLabelStackFully } from './common/utils';
import { createCriticalMetricsPayloads } from './critical-metrics-payload';
import { addPerformanceMeasures } from './utils/add-performance-measures';
import { getBrowserMetadataToLegacyFormat } from './utils/get-browser-metadata';
import getInteractionStatus from './utils/get-interaction-status';
import { getMoreAccuratePageVisibilityUpToTTAI } from './utils/get-more-accurate-page-visibility-up-to-ttai';
import { getNavigationMetricsToLegacyFormat } from './utils/get-navigation-metrics';
import getPageVisibilityUpToTTAI from './utils/get-page-visibility-up-to-ttai';
import { getPaintMetricsToLegacyFormat } from './utils/get-paint-metrics';
import getPayloadSize from './utils/get-payload-size';
import { getReactUFOPayloadVersion } from './utils/get-react-ufo-payload-version';
import getSSRDoneTimeValue from './utils/get-ssr-done-time-value';
import getSSRSuccessUtil from './utils/get-ssr-success';
import getTTAI from './utils/get-ttai';
import getVCMetrics from './utils/get-vc-metrics';
import { getVisibilityStateFromPerformance } from './utils/get-visibility-state-from-performance';
import { optimizeApdex } from './utils/optimize-apdex';
import { optimizeCustomTimings } from './utils/optimize-custom-timings';
import { optimizeHoldInfo } from './utils/optimize-hold-info';
import { optimizeMarks } from './utils/optimize-marks';
import { optimizeReactProfilerTimings } from './utils/optimize-react-profiler-timings';
import { optimizeRequestInfo } from './utils/optimize-request-info';
import { optimizeSpans } from './utils/optimize-spans';
function getUfoNameOverride(interaction) {
  var ufoName = interaction.ufoName,
    apdex = interaction.apdex;
  try {
    var ufoNameOverrides = getUfoNameOverrides();
    if (ufoNameOverrides != null) {
      var metricKey = apdex.length > 0 ? apdex[0].key : '';
      if (ufoNameOverrides[ufoName][metricKey]) {
        return ufoNameOverrides[ufoName][metricKey];
      }
    }
    return ufoName;
  } catch (e) {
    return ufoName;
  }
}
function getEarliestLegacyStopTime(interaction, labelStack) {
  var earliestLegacyStopTime = null;
  interaction.apdex.forEach(function (a) {
    var _a$labelStack;
    if (!(a !== null && a !== void 0 && a.stopTime)) {
      return;
    }
    if (!labelStackStartWith((_a$labelStack = a.labelStack) !== null && _a$labelStack !== void 0 ? _a$labelStack : [], labelStack)) {
      return;
    }
    if (a.stopTime > interaction.start && (earliestLegacyStopTime !== null && earliestLegacyStopTime !== void 0 ? earliestLegacyStopTime : a.stopTime) >= a.stopTime) {
      earliestLegacyStopTime = a.stopTime;
    }
  });
  return earliestLegacyStopTime;
}
function getBm3EndTimeOrFallbackValue(interaction) {
  var _getEarliestLegacySto;
  var labelStack = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var fallbackValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : interaction.end;
  if (interaction.type === 'press') {
    return fallbackValue;
  }
  return (_getEarliestLegacySto = getEarliestLegacyStopTime(interaction, labelStack)) !== null && _getEarliestLegacySto !== void 0 ? _getEarliestLegacySto : fallbackValue;
}
function getPageVisibilityUpToTTI(interaction) {
  var start = interaction.start;
  var bm3EndTimeOrInteractionEndTime = getBm3EndTimeOrFallbackValue(interaction);
  return getPageVisibilityState(start, bm3EndTimeOrInteractionEndTime);
}
function getMoreAccuratePageVisibilityUpToTTI(interaction) {
  var old = getPageVisibilityUpToTTI(interaction);
  var tti = getEarliestLegacyStopTime(interaction, []);
  if (!tti) {
    return old;
  }
  var buffered = getVisibilityStateFromPerformance(tti);
  if (!buffered) {
    return old;
  }
  if (buffered !== old) {
    return 'mixed';
  }
  return old;
}
function getResourceTimings(start, end) {
  var _resourceTiming$getRe;
  return (_resourceTiming$getRe = resourceTiming.getResourceTimings(start, end)) !== null && _resourceTiming$getRe !== void 0 ? _resourceTiming$getRe : undefined;
}
function getBundleEvalTimings(start) {
  return bundleEvalTiming.getBundleEvalTimings(start);
}
function getSSRPhaseSuccess(type) {
  return type === 'page_load' ? ssr.getSSRPhaseSuccess() : undefined;
}
function getSSRFeatureFlags(type) {
  return type === 'page_load' ? ssr.getSSRFeatureFlags() : undefined;
}
function getPPSMetrics(interaction) {
  var _interaction$apdex;
  var start = interaction.start,
    end = interaction.end;
  var config = getConfig();
  var interactionStatus = getInteractionStatus(interaction);
  var pageVisibilityUpToTTAI = getPageVisibilityUpToTTAI(interaction);
  var tti = (_interaction$apdex = interaction.apdex) === null || _interaction$apdex === void 0 || (_interaction$apdex = _interaction$apdex[0]) === null || _interaction$apdex === void 0 ? void 0 : _interaction$apdex.stopTime;
  var ttai = interactionStatus.originalInteractionStatus === 'SUCCEEDED' && pageVisibilityUpToTTAI === 'visible' ? Math.round(end - start) : undefined;
  var PPSMetricsAtTTI = tti !== undefined ? getLighthouseMetrics({
    start: start,
    stop: tti
  }) : null;
  var PPSMetricsAtTTAI = ttai !== undefined ? getLighthouseMetrics({
    start: start,
    stop: interaction.end
  }) : null;
  if (fg('platform_ufo_remove_deprecated_config_fields')) {
    if (PPSMetricsAtTTAI !== null) {
      return PPSMetricsAtTTAI;
    }
  } else {
    if (config !== null && config !== void 0 && config.shouldCalculateLighthouseMetricsFromTTAI && PPSMetricsAtTTAI !== null) {
      return PPSMetricsAtTTAI;
    }
    if (PPSMetricsAtTTI !== null) {
      return _objectSpread(_objectSpread({}, PPSMetricsAtTTI), {}, {
        'metrics@ttai': PPSMetricsAtTTAI
      });
    }
  }
  return {};
}
function getSSRProperties(type) {
  var ssrPhases = getSSRPhaseSuccess(type);
  return _objectSpread(_objectSpread({
    'ssr:success': (ssrPhases === null || ssrPhases === void 0 ? void 0 : ssrPhases.done) != null ? ssrPhases.done : getSSRSuccessUtil(type),
    'ssr:featureFlags': getSSRFeatureFlags(type)
  }, (ssrPhases === null || ssrPhases === void 0 ? void 0 : ssrPhases.earlyFlush) != null ? {
    'ssr:earlyflush:success': ssrPhases.earlyFlush
  } : null), (ssrPhases === null || ssrPhases === void 0 ? void 0 : ssrPhases.prefetch) != null ? {
    'ssr:prefetch:success': ssrPhases.prefetch
  } : null);
}
function getAssetsMetrics(interaction, SSRDoneTime) {
  try {
    var config = getConfig();
    var type = interaction.type;
    var allowedTypes = ['page_load'];
    var assetsConfig = config === null || config === void 0 ? void 0 : config.assetsConfig;
    if (!allowedTypes.includes(type) || !assetsConfig) {
      // Skip if: type not allowed or assetsClassification isn't configured
      return {};
    }
    var reporter = new CHRReporter();
    var resourceTimings = filterResourceTimings(interaction.start, interaction.end);
    var assets = reporter.get(resourceTimings, assetsConfig, SSRDoneTime);
    if (assets) {
      // Only add assets in case it exists
      return {
        'event:assets': assets
      };
    }
    return {};
  } catch (error) {
    // Skip CHR in case of error
    return {};
  }
}
function getTracingContextData(interaction) {
  var trace = interaction.trace,
    start = interaction.start;
  var tracingContextData = {};
  if (trace) {
    tracingContextData = {
      'ufo:tracingContext': {
        'X-B3-TraceId': trace.traceId,
        'X-B3-SpanId': trace.spanId,
        // eslint-disable-next-line compat/compat
        browserTimeOrigin: +(performance.timeOrigin + start).toFixed(2)
      }
    };
  }
  return tracingContextData;
}
function optimizeCustomData(interaction) {
  var customData = interaction.customData,
    cohortingCustomData = interaction.cohortingCustomData,
    legacyMetrics = interaction.legacyMetrics;
  var customDataMap = customData.reduce(function (result, _ref) {
    var _result$get$data, _result$get;
    var labelStack = _ref.labelStack,
      data = _ref.data;
    var label = stringifyLabelStackFully(labelStack);
    var value = (_result$get$data = (_result$get = result.get(label)) === null || _result$get === void 0 ? void 0 : _result$get.data) !== null && _result$get$data !== void 0 ? _result$get$data : {};
    result.set(label, {
      labelStack: optimizeLabelStack(labelStack, getReactUFOPayloadVersion(interaction.type)),
      data: Object.assign(value, data)
    });
    return result;
  }, new Map());

  // Merge cohorting custom data into the same map
  if (cohortingCustomData && cohortingCustomData.size > 0) {
    var _interaction$labelSta, _customDataMap$get$da, _customDataMap$get, _interaction$labelSta2;
    var label = stringifyLabelStackFully((_interaction$labelSta = interaction.labelStack) !== null && _interaction$labelSta !== void 0 ? _interaction$labelSta : []);
    var value = (_customDataMap$get$da = (_customDataMap$get = customDataMap.get(label)) === null || _customDataMap$get === void 0 ? void 0 : _customDataMap$get.data) !== null && _customDataMap$get$da !== void 0 ? _customDataMap$get$da : {};
    customDataMap.set(label, {
      labelStack: optimizeLabelStack((_interaction$labelSta2 = interaction.labelStack) !== null && _interaction$labelSta2 !== void 0 ? _interaction$labelSta2 : [], getReactUFOPayloadVersion(interaction.type)),
      data: Object.assign(value, Object.fromEntries(cohortingCustomData))
    });
  }
  if (legacyMetrics) {
    var legacyMetricsFiltered = legacyMetrics.filter(function (item) {
      return item.type === 'PAGE_LOAD';
    }).reduce(function (result, currentValue) {
      for (var _i = 0, _Object$entries = Object.entries(currentValue.custom || {}); _i < _Object$entries.length; _i++) {
        var _result$get$data2, _result$get2;
        var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
          key = _Object$entries$_i[0],
          _value = _Object$entries$_i[1];
        var _label = stringifyLabelStackFully([]);
        var labelValue = (_result$get$data2 = (_result$get2 = result.get(_label)) === null || _result$get2 === void 0 ? void 0 : _result$get2.data) !== null && _result$get$data2 !== void 0 ? _result$get$data2 : {};
        result.set(_label, {
          labelStack: optimizeLabelStack([], getReactUFOPayloadVersion(interaction.type)),
          data: Object.assign(labelValue, _defineProperty({}, key, _value))
        });
      }
      return result;
    }, new Map());
    return [].concat(_toConsumableArray(customDataMap.values()), _toConsumableArray(legacyMetricsFiltered.values()));
  }
  return _toConsumableArray(customDataMap.values());
}
function optimizeRedirects(redirects, interactionStart) {
  var lastRedirectTime = interactionStart;
  var updatedRedirects = redirects.sort(function (a, b) {
    return a.time - b.time;
  }).reduce(function (result, redirect) {
    var fromInteractionName = redirect.fromInteractionName,
      time = redirect.time;
    if (lastRedirectTime >= interactionStart) {
      result.push({
        labelStack: [{
          n: fromInteractionName
        }],
        startTime: Math.round(lastRedirectTime),
        endTime: Math.round(time)
      });
    }
    lastRedirectTime = time;
    return result;
  }, []);
  return updatedRedirects;
}
function objectToArray() {
  var obj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Object.keys(obj).reduce(function (result, key) {
    result.push({
      label: key,
      data: obj[key]
    });
    return result;
  }, []);
}
function getBM3SubmetricsTimings(submetrics) {
  if (!submetrics) {
    return null;
  }
  var submetricsTimings = submetrics.filter(function (item) {
    return typeof item.stop === 'number' && !!item.key && typeof item.start === 'number';
  }).map(function (item) {
    var childSubmetrics;
    var newKey = "include/".concat(item.key);
    if (item.submetrics) {
      childSubmetrics = getBM3SubmetricsTimings(item.submetrics);
    }
    return _objectSpread(_defineProperty({}, newKey, {
      endTime: item.stop - item.start,
      startTime: item.start
    }), childSubmetrics ? childSubmetrics : {});
  });
  return submetricsTimings;
}
function getBm3TrackerTimings(interaction) {
  var interactionLegacyMetrics = interaction.legacyMetrics;
  if (!interactionLegacyMetrics) {
    return {};
  }
  var legacyMetrics = interactionLegacyMetrics.map(function (item) {
    var _item$config, _item$config2, _item$marks;
    return {
      key: item.key,
      startTime: item.start,
      stopTime: item.stop,
      type: (_item$config = item.config) === null || _item$config === void 0 ? void 0 : _item$config.type,
      reactUFOName: (_item$config2 = item.config) === null || _item$config2 === void 0 ? void 0 : _item$config2.reactUFOName,
      fmp: ((_item$marks = item.marks) === null || _item$marks === void 0 ? void 0 : _item$marks['fmp']) || item.stop,
      source: 'bm3',
      timings: getBm3Timings(item.marks, item.config.timings),
      submetrics: getBM3SubmetricsTimings(item.submetrics),
      pageVisibleState: item.pageVisibleState
    };
  }).filter(function (item) {
    return !!item.type;
  });
  return {
    legacyMetrics: legacyMetrics
  };
}
function getStylesheetMetrics() {
  try {
    var doc = getDocument();
    if (!doc) {
      return {};
    }
    var stylesheets = Array.from(doc.styleSheets);
    var stylesheetCount = stylesheets.length;
    var cssrules = Array.from(doc.styleSheets).reduce(function (acc, item) {
      // Other domain stylesheets throw a SecurityError
      try {
        return acc + item.cssRules.length;
      } catch (e) {
        return acc;
      }
    }, 0);
    var styleElements = doc.querySelectorAll('style').length;
    var styleProps = doc.querySelectorAll('[style]');
    var styleDeclarations = Array.from(doc.querySelectorAll('[style]')).reduce(function (acc, item) {
      try {
        if ('style' in item) {
          return acc + item.style.length;
        } else {
          return acc;
        }
      } catch (e) {
        return acc;
      }
    }, 0);
    return {
      'ufo:stylesheets': stylesheetCount,
      'ufo:styleElements': styleElements,
      'ufo:styleProps': styleProps.length,
      'ufo:styleDeclarations': styleDeclarations,
      'ufo:cssrules': cssrules
    };
  } catch (e) {
    return {};
  }
}
var regularTTAI;
var expTTAI;
function getErrorCounts(interaction) {
  return {
    'ufo:errors:globalCount': getGlobalErrorCount(),
    'ufo:errors:count': interaction.errors.length
  };
}
function createInteractionMetricsPayload(_x, _x2, _x3, _x4, _x5) {
  return _createInteractionMetricsPayload.apply(this, arguments);
}
function _createInteractionMetricsPayload() {
  _createInteractionMetricsPayload = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(interaction, interactionId, experimental, criticalPayloadCount, vcMetrics) {
    var _window$location, _config$additionalPay;
    var interactionPayloadStart, config, end, start, ufoName, knownSegments, rate, type, abortReason, routeName, featureFlags, previousInteractionName, isPreviousInteractionAborted, abortedByInteractionName, responsiveness, unknownElementName, unknownElementHierarchy, hydration, pageVisibilityAtTTI, pageVisibilityAtTTAI, segments, segmentTree, isDetailedPayload, isPageLoad, calculatePageVisibilityFromTheStartOfPageLoad, moreAccuratePageVisibilityAtTTI, moreAccuratePageVisibilityAtTTAI, labelStack, getInitialPageLoadSSRMetrics, pageLoadInteractionMetrics, getDetailedInteractionMetrics, getPageLoadDetailedInteractionMetrics, newUFOName, resourceTimings, _yield$Promise$all, _yield$Promise$all2, finalVCMetrics, experimentalMetrics, paintMetrics, getReactHydrationStats, payload;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          interactionPayloadStart = performance.now();
          config = getConfig();
          if (config) {
            _context.next = 4;
            break;
          }
          throw Error('UFO Configuration not provided');
        case 4:
          end = interaction.end, start = interaction.start, ufoName = interaction.ufoName, knownSegments = interaction.knownSegments, rate = interaction.rate, type = interaction.type, abortReason = interaction.abortReason, routeName = interaction.routeName, featureFlags = interaction.featureFlags, previousInteractionName = interaction.previousInteractionName, isPreviousInteractionAborted = interaction.isPreviousInteractionAborted, abortedByInteractionName = interaction.abortedByInteractionName, responsiveness = interaction.responsiveness, unknownElementName = interaction.unknownElementName, unknownElementHierarchy = interaction.unknownElementHierarchy, hydration = interaction.hydration;
          pageVisibilityAtTTI = getPageVisibilityUpToTTI(interaction);
          pageVisibilityAtTTAI = getPageVisibilityUpToTTAI(interaction);
          segments = !fg('platform_ufo_remove_deprecated_config_fields') && config.killswitchNestedSegments ? [] : knownSegments;
          segmentTree = getReactUFOPayloadVersion(interaction.type) === '2.0.0' ? buildSegmentTree(segments.map(function (segment) {
            return segment.labelStack;
          })) : {};
          isDetailedPayload = pageVisibilityAtTTAI === 'visible';
          isPageLoad = type === 'page_load';
          calculatePageVisibilityFromTheStartOfPageLoad = config.enableBetterPageVisibilityApi && isPageLoad;
          moreAccuratePageVisibilityAtTTI = calculatePageVisibilityFromTheStartOfPageLoad ? getMoreAccuratePageVisibilityUpToTTI(interaction) : null;
          moreAccuratePageVisibilityAtTTAI = calculatePageVisibilityFromTheStartOfPageLoad ? getMoreAccuratePageVisibilityUpToTTAI(interaction) : null;
          labelStack = interaction.labelStack ? {
            labelStack: optimizeLabelStack(interaction.labelStack, getReactUFOPayloadVersion(interaction.type))
          } : {}; // Page Load
          getInitialPageLoadSSRMetrics = function getInitialPageLoadSSRMetrics() {
            var _config$ssr;
            if (!isPageLoad) {
              return {};
            }
            var config = getConfig();
            var SSRDoneTimeValue = getSSRDoneTimeValue(config);
            var SSRDoneTime = SSRDoneTimeValue !== undefined ? {
              SSRDoneTime: Math.round(SSRDoneTimeValue)
            } : {};
            var isBM3ConfigSSRDoneAsFmp = interaction.metaData.__legacy__bm3ConfigSSRDoneAsFmp;
            var isUFOConfigSSRDoneAsFmp = interaction.metaData.__legacy__bm3ConfigSSRDoneAsFmp || !!(config !== null && config !== void 0 && (_config$ssr = config.ssr) !== null && _config$ssr !== void 0 && _config$ssr.getSSRDoneTime);
            if (!experimental && (isBM3ConfigSSRDoneAsFmp || isUFOConfigSSRDoneAsFmp) && SSRDoneTimeValue !== undefined) {
              try {
                performance.mark("FMP", {
                  startTime: SSRDoneTimeValue,
                  detail: {
                    devtools: {
                      dataType: 'marker'
                    }
                  }
                });
              } catch (e) {}
            }
            return _objectSpread(_objectSpread({}, SSRDoneTime), {}, {
              isBM3ConfigSSRDoneAsFmp: isBM3ConfigSSRDoneAsFmp,
              isUFOConfigSSRDoneAsFmp: isUFOConfigSSRDoneAsFmp
            });
          };
          pageLoadInteractionMetrics = getInitialPageLoadSSRMetrics(); // Detailed payload. Page visibility = visible
          getDetailedInteractionMetrics = function getDetailedInteractionMetrics(resourceTimings) {
            if (experimental || window.__UFO_COMPACT_PAYLOAD__ || !isDetailedPayload) {
              return {};
            }
            var spans = [].concat(_toConsumableArray(interaction.spans), _toConsumableArray(atlaskitInteractionSpans));
            atlaskitInteractionSpans.length = 0;
            return {
              errors: interaction.errors.map(function (_ref2) {
                var labelStack = _ref2.labelStack,
                  others = _objectWithoutProperties(_ref2, _excluded);
                return _objectSpread(_objectSpread({}, others), {}, {
                  labelStack: labelStack && optimizeLabelStack(labelStack, getReactUFOPayloadVersion(interaction.type))
                });
              }),
              holdActive: _toConsumableArray(interaction.holdActive.values()),
              redirects: optimizeRedirects(interaction.redirects, start),
              holdInfo: optimizeHoldInfo(experimental ? interaction.holdExpInfo : interaction.holdInfo, start, getReactUFOPayloadVersion(interaction.type)),
              spans: optimizeSpans(spans, start, getReactUFOPayloadVersion(interaction.type)),
              requestInfo: optimizeRequestInfo(interaction.requestInfo, start, getReactUFOPayloadVersion(interaction.type)),
              customTimings: optimizeCustomTimings(interaction.customTimings, start),
              bundleEvalTimings: objectToArray(getBundleEvalTimings(start)),
              resourceTimings: objectToArray(resourceTimings)
            };
          }; // Page load & detailed payload
          getPageLoadDetailedInteractionMetrics = function getPageLoadDetailedInteractionMetrics() {
            var _config$ssr2;
            if (!isPageLoad || !isDetailedPayload) {
              return {};
            }
            var config = getConfig();
            return {
              initialPageLoadExtraTimings: objectToArray(initialPageLoadExtraTiming.getTimings()),
              SSRTimings: config !== null && config !== void 0 && (_config$ssr2 = config.ssr) !== null && _config$ssr2 !== void 0 && _config$ssr2.getSSRTimings ? config.ssr.getSSRTimings() : objectToArray(ssr.getSSRTimings())
            };
          };
          if (experimental) {
            expTTAI = getTTAI(interaction);
          } else {
            regularTTAI = getTTAI(interaction);
          }
          newUFOName = sanitizeUfoName(ufoName);
          resourceTimings = getResourceTimings(start, end);
          _context.t0 = Promise;
          _context.t1 = vcMetrics;
          if (_context.t1) {
            _context.next = 28;
            break;
          }
          _context.next = 27;
          return getVCMetrics(interaction);
        case 27:
          _context.t1 = _context.sent;
        case 28:
          _context.t2 = _context.t1;
          _context.t3 = experimental ? getExperimentalVCMetrics(interaction) : Promise.resolve(undefined);
          _context.t4 = getPaintMetricsToLegacyFormat(type, end);
          _context.t5 = [_context.t2, _context.t3, _context.t4];
          _context.next = 34;
          return _context.t0.all.call(_context.t0, _context.t5);
        case 34:
          _yield$Promise$all = _context.sent;
          _yield$Promise$all2 = _slicedToArray(_yield$Promise$all, 3);
          finalVCMetrics = _yield$Promise$all2[0];
          experimentalMetrics = _yield$Promise$all2[1];
          paintMetrics = _yield$Promise$all2[2];
          if (!experimental) {
            addPerformanceMeasures(interaction.start, _toConsumableArray((finalVCMetrics === null || finalVCMetrics === void 0 ? void 0 : finalVCMetrics['ufo:vc:rev']) || []));
          }
          getReactHydrationStats = function getReactHydrationStats() {
            if (!hydration) {
              return {};
            }
            return {
              hydration: hydration
            };
          };
          payload = {
            actionSubject: 'experience',
            action: 'measured',
            eventType: 'operational',
            source: 'measured',
            tags: ['observability'],
            attributes: {
              properties: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
                // basic
                'event:hostname': ((_window$location = window.location) === null || _window$location === void 0 ? void 0 : _window$location.hostname) || 'unknown',
                'event:product': config.product,
                'event:schema': '1.0.0',
                'event:sizeInKb': 0,
                'event:source': {
                  name: 'react-ufo/web',
                  version: getReactUFOPayloadVersion(interaction.type)
                },
                'event:region': config.region || 'unknown',
                'experience:key': experimental ? 'custom.experimental-interaction-metrics' : 'custom.interaction-metrics',
                'experience:name': newUFOName,
                // Include CPU usage monitoring data
                'event:cpu:usage': createPressureStateReport(interaction.start, interaction.end),
                'event:memory:usage': createMemoryStateReport(interaction.start, interaction.end)
              }, criticalPayloadCount !== undefined ? {
                'ufo:multipayload': true,
                'ufo:criticalPayloadCount': criticalPayloadCount
              } : {}), getBrowserMetadataToLegacyFormat()), getSSRProperties(type)), getAssetsMetrics(interaction, pageLoadInteractionMetrics === null || pageLoadInteractionMetrics === void 0 ? void 0 : pageLoadInteractionMetrics.SSRDoneTime)), getPPSMetrics(interaction)), paintMetrics), getNavigationMetricsToLegacyFormat(type)), finalVCMetrics), experimentalMetrics), (_config$additionalPay = config.additionalPayloadData) === null || _config$additionalPay === void 0 ? void 0 : _config$additionalPay.call(config, interaction)), getTracingContextData(interaction)), getStylesheetMetrics()), getErrorCounts(interaction)), getReactHydrationStats()), {}, {
                interactionMetrics: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
                  namePrefix: config.namePrefix || '',
                  segmentPrefix: config.segmentPrefix || '',
                  interactionId: interactionId,
                  pageVisibilityAtTTI: pageVisibilityAtTTI,
                  pageVisibilityAtTTAI: pageVisibilityAtTTAI,
                  experimental__pageVisibilityAtTTI: moreAccuratePageVisibilityAtTTI,
                  experimental__pageVisibilityAtTTAI: moreAccuratePageVisibilityAtTTAI,
                  // raw interaction metrics
                  rate: rate,
                  routeName: routeName,
                  type: type,
                  abortReason: abortReason,
                  featureFlags: featureFlags,
                  previousInteractionName: previousInteractionName,
                  isPreviousInteractionAborted: isPreviousInteractionAborted,
                  abortedByInteractionName: abortedByInteractionName,
                  // performance
                  apdex: optimizeApdex(interaction.apdex, getReactUFOPayloadVersion(interaction.type)),
                  end: Math.round(end),
                  start: Math.round(start),
                  segments: getReactUFOPayloadVersion(interaction.type) === '2.0.0' ? segmentTree : getOldSegmentsLabelStack(segments, interaction.type),
                  marks: optimizeMarks(interaction.marks, getReactUFOPayloadVersion(interaction.type)),
                  customData: optimizeCustomData(interaction),
                  reactProfilerTimings: optimizeReactProfilerTimings(interaction.reactProfilerTimings, start, getReactUFOPayloadVersion(interaction.type))
                }, responsiveness ? {
                  responsiveness: responsiveness
                } : {}), labelStack), pageLoadInteractionMetrics), getDetailedInteractionMetrics(resourceTimings)), getPageLoadDetailedInteractionMetrics()), getBm3TrackerTimings(interaction)), {}, {
                  'metric:ttai': experimental ? regularTTAI || expTTAI : undefined,
                  'metric:experimental:ttai': expTTAI
                }, unknownElementName ? {
                  unknownElementName: unknownElementName
                } : {}), unknownElementHierarchy ? {
                  unknownElementHierarchy: unknownElementHierarchy
                } : {}), fg('platform_ufo_enable_minor_interactions') ? {
                  minorInteractions: interaction.minorInteractions
                } : {}),
                'ufo:payloadTime': roundEpsilon(performance.now() - interactionPayloadStart)
              })
            }
          };
          if (experimental) {
            regularTTAI = undefined;
            expTTAI = undefined;
          }
          payload.attributes.properties['event:sizeInKb'] = getPayloadSize(payload.attributes.properties);
          return _context.abrupt("return", payload);
        case 45:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return _createInteractionMetricsPayload.apply(this, arguments);
}
export function createPayloads(_x6, _x7) {
  return _createPayloads.apply(this, arguments);
}
function _createPayloads() {
  _createPayloads = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(interactionId, interaction) {
    var ufoNameOverride, modifiedInteraction, payloads, isCriticalMetricsEnabled, vcMetrics, criticalMetricsPayloads, criticalPayloadCount, interactionMetricsPayload;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          ufoNameOverride = getUfoNameOverride(interaction);
          modifiedInteraction = _objectSpread(_objectSpread({}, interaction), {}, {
            ufoName: ufoNameOverride
          });
          payloads = [];
          isCriticalMetricsEnabled = fg('platform_ufo_critical_metrics_payload'); // Calculate VC metrics once to avoid duplicate expensive calculations
          _context2.next = 6;
          return getVCMetrics(interaction);
        case 6:
          vcMetrics = _context2.sent;
          if (!isCriticalMetricsEnabled) {
            _context2.next = 13;
            break;
          }
          _context2.next = 10;
          return createCriticalMetricsPayloads(interactionId, interaction, vcMetrics);
        case 10:
          _context2.t0 = _context2.sent;
          _context2.next = 14;
          break;
        case 13:
          _context2.t0 = [];
        case 14:
          criticalMetricsPayloads = _context2.t0;
          payloads.push.apply(payloads, _toConsumableArray(criticalMetricsPayloads));
          criticalPayloadCount = isCriticalMetricsEnabled ? criticalMetricsPayloads.length : undefined;
          _context2.next = 19;
          return createInteractionMetricsPayload(modifiedInteraction, interactionId, undefined, criticalPayloadCount, vcMetrics);
        case 19:
          interactionMetricsPayload = _context2.sent;
          payloads.push(interactionMetricsPayload);
          return _context2.abrupt("return", payloads.filter(Boolean));
        case 22:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  return _createPayloads.apply(this, arguments);
}
export function createExperimentalMetricsPayload(_x8, _x9) {
  return _createExperimentalMetricsPayload.apply(this, arguments);
}
function _createExperimentalMetricsPayload() {
  _createExperimentalMetricsPayload = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(interactionId, interaction) {
    var config, ufoName, rate, pageVisibilityState, result;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          config = getConfig();
          if (config) {
            _context3.next = 3;
            break;
          }
          throw Error('UFO Configuration not provided');
        case 3:
          ufoName = sanitizeUfoName(interaction.ufoName);
          rate = getExperimentalInteractionRate(ufoName, interaction.type);
          if (coinflip(rate)) {
            _context3.next = 7;
            break;
          }
          return _context3.abrupt("return", null);
        case 7:
          pageVisibilityState = getPageVisibilityState(interaction.start, interaction.end);
          if (!(pageVisibilityState !== 'visible')) {
            _context3.next = 10;
            break;
          }
          return _context3.abrupt("return", null);
        case 10:
          _context3.next = 12;
          return createInteractionMetricsPayload(interaction, interactionId, true);
        case 12:
          result = _context3.sent;
          return _context3.abrupt("return", result);
        case 14:
        case "end":
          return _context3.stop();
      }
    }, _callee3);
  }));
  return _createExperimentalMetricsPayload.apply(this, arguments);
}
export function createExtraSearchPageInteractionPayload(_x0, _x1) {
  return _createExtraSearchPageInteractionPayload.apply(this, arguments);
}
function _createExtraSearchPageInteractionPayload() {
  _createExtraSearchPageInteractionPayload = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(interactionId, interaction) {
    var SAIN_HOLD_NAMES, NAME_OVERRIDE, SEARCH_PAGE_SMART_ANSWERS_SEGMENT_LABEL, newInteractionId, newEnd, holdInfo, reactProfilerTimings, lastHold, isLastHoldSAIN, lastFilteredTime, filteredReactProfilerTimings, lastTiming, modifiedInteraction, payloads, vcMetrics, interactionMetricsPayload;
    return _regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) switch (_context4.prev = _context4.next) {
        case 0:
          SAIN_HOLD_NAMES = ['search-ai-dialog-visible-text-loading', 'search-ai-dialog-all-text-loading'];
          NAME_OVERRIDE = 'search-page-ignoring-smart-answers';
          SEARCH_PAGE_SMART_ANSWERS_SEGMENT_LABEL = 'search-page-smart-answers';
          newInteractionId = "".concat(interactionId, "-ignoring-smart-answers"); // Calculate a new end time which excludes SAIN holds
          holdInfo = interaction.holdInfo, reactProfilerTimings = interaction.reactProfilerTimings;
          lastHold = holdInfo.at(-1);
          isLastHoldSAIN = Boolean(lastHold && SAIN_HOLD_NAMES.includes(lastHold.name)); // A new end time is only calculated if the last hold is a SAIN hold
          if (isLastHoldSAIN) {
            lastFilteredTime = null;
            filteredReactProfilerTimings = reactProfilerTimings.filter(function (timing) {
              if (timing.commitTime === lastFilteredTime) {
                return false;
              }
              var isTimingSmartAnswersInSearch = timing.labelStack.some(function (label) {
                return label.name === SEARCH_PAGE_SMART_ANSWERS_SEGMENT_LABEL;
              });
              if (isTimingSmartAnswersInSearch) {
                lastFilteredTime = timing.commitTime;
                return false;
              }
              return true;
            });
            lastTiming = filteredReactProfilerTimings.at(-1);
            if (lastTiming) {
              newEnd = lastTiming.commitTime;
            }
          }
          modifiedInteraction = _objectSpread(_objectSpread({}, interaction), {}, {
            end: newEnd !== null && newEnd !== void 0 ? newEnd : interaction.end,
            holdInfo: [],
            knownSegments: [],
            reactProfilerTimings: [],
            ufoName: NAME_OVERRIDE
          });
          payloads = [];
          _context4.next = 12;
          return getVCMetrics(interaction, false, true);
        case 12:
          vcMetrics = _context4.sent;
          _context4.next = 15;
          return createInteractionMetricsPayload(modifiedInteraction, newInteractionId, undefined, undefined, vcMetrics);
        case 15:
          interactionMetricsPayload = _context4.sent;
          payloads.push(interactionMetricsPayload);
          return _context4.abrupt("return", payloads.filter(Boolean));
        case 18:
        case "end":
          return _context4.stop();
      }
    }, _callee4);
  }));
  return _createExtraSearchPageInteractionPayload.apply(this, arguments);
}